import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { get } from '../../../api/axios';
import endpoints from '../../../api/endpoints';
import { getApiErrorMessage } from '../../../helpers/apiError';
import { getUserAvatarUrl } from '../../../helpers/userAvatar';
import { getUserFullName } from '../../../helpers/userDisplay';
import ProjectCollaborators from '../../../components/DashboardComponents/ProjectCollaborators';

const PAGE_SIZE = 10;
const DEFAULT_COLLAB_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_SORT_BY = 'created_at';
const DEFAULT_SORT_DIRECTION = 'desc';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      });
};

const capitalizeLabel = (value) => {
  const label = String(value || '').trim();
  if (!label) return '';
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const getRoleFields = (invitation) => {
  const roleType =
    invitation.role_types?.[0] ||
    invitation.role_type ||
    null;
  const role =
    capitalizeLabel(
      roleType?.public_name ||
        roleType?.name ||
        invitation.role ||
        ''
    ) || '—';
  const roleDescription =
    String(
      invitation.description ||
        roleType?.description ||
        roleType?.public_description ||
        ''
    ).trim() || '—';

  return {
    role,
    roleDescription,
    roleId: roleType?.id || invitation.role_type_id || null,
    roleName:
      roleType?.public_name || roleType?.name || invitation.role || '',
  };
};

const formatCityValue = (city) => {
  if (!city) return '';
  if (typeof city === 'string') return city;
  const cityName = city.city || city.name || '';
  const region = city.state || city.country || '';
  if (cityName && region) return `${cityName}, ${region}`;
  return cityName || region || '';
};

const memberCity = (user = {}, project = {}) => {
  const fromUser = formatCityValue(
    user.address?.city ||
      user.addresses?.[0]?.city ||
      user.city ||
      user.primary_city ||
      null
  );
  if (fromUser) return fromUser;

  const fromProject = formatCityValue(
    project.city ||
      project.primary_city ||
      project.cities?.[0] ||
      project.location ||
      null
  );
  return fromProject || '—';
};

const mapInvitationToCollaborator = (invitation) => {
  const user = invitation.user || {};
  const invite = invitation.invite || {};
  const project = invitation.project || {};
  const hasUser = Boolean(
    user?.id || user?.email || user?.first_name || user?.full_name
  );
  const inviteEmail =
    invite.email ||
    invite.invitee_email ||
    invite.invited_email ||
    invitation.email ||
    invitation.invitee_email ||
    '';

  const name = hasUser
    ? getUserFullName(user, { fallback: 'Unknown' })
    : inviteEmail || 'External collaborator';

  const { role, roleDescription, roleId, roleName } = getRoleFields(invitation);

  return {
    id: invitation.id,
    memberId: invitation.id,
    userId: user.id || invitation.user_id || null,
    name,
    avatar: hasUser ? getUserAvatarUrl(user) : null,
    city: memberCity(user, project),
    role,
    roleDescription,
    roleId,
    roleName,
    estimatedHours: invitation.estimated_hours ?? '',
    hoursCadence: invitation.hours_cadence || 'per_week',
    description: String(invitation.description || '').trim(),
    addedOn: formatDate(invitation.joined_at || invitation.created_at),
    status: invitation.status || 'pending',
    isExternal: !hasUser,
  };
};

/**
 * Collaborators tab — fetches project invitations with server pagination,
 * search, status filter, and sort (same pattern as Projects listing).
 */
const ProjectCollaboratorsTab = () => {
  const { projectId: routeProjectId } = useParams();
  const { project, collabModal, setCollabModal, collabRefreshNonce, registerTabRefresh } =
    useOutletContext();
  const projectId = routeProjectId || project?.id;
  const collaboratorLimit =
    Number(project?.collaborator_limit) > 0
      ? Number(project.collaborator_limit)
      : DEFAULT_COLLAB_LIMIT;

  const [collaborators, setCollaborators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState("");
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearchQuery(searchQuery.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleStatusFiltersChange = (next) => {
    setStatusFilters(Array.isArray(next) ? String(next[0] || "") : String(next || ""));
    setCurrentPage(1);
  };

  const handleSortByChange = (next) => {
    setSortBy(next);
    setCurrentPage(1);
  };

  const handleSortDirectionChange = (next) => {
    setSortDirection(next);
    setCurrentPage(1);
  };

  const fetchCollaborators = useCallback(async () => {
    if (!projectId) {
      setCollaborators([]);
      setTotalItems(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = {
        involvement: 'all',
        project_id: projectId,
        page: currentPage,
        per_page: PAGE_SIZE,
        page_size: PAGE_SIZE,
        'with[]': ['user.address.city', 'project', 'invite', 'roleTypes'],
        sort_by: sortBy,
        sort_direction: sortDirection,
      };

      if (debouncedSearchQuery) {
        params.search = debouncedSearchQuery;
      }

      // Axios already serializes arrays as status[]=… — do not use key "status[]"
      if (statusFilters) {
        params.status = statusFilters;
      }

      const response = await get(endpoints.getProjectInvitations, { params });

      if (response?.data?.success) {
        const result = response.data.result || {};
        const rows = (result.data || []).map(mapInvitationToCollaborator);
        const total = result.total || 0;
        const perPage = result.per_page || result.page_size || PAGE_SIZE;

        setCollaborators(rows);
        setTotalItems(total);
        setPageSize(perPage);

        const totalPages = Math.max(1, Math.ceil(total / perPage));
        if (currentPage > totalPages) {
          setCurrentPage(totalPages);
        }
      } else {
        const message = getApiErrorMessage(
          response,
          'Failed to load collaborators'
        );
        setError(message);
        setCollaborators([]);
        setTotalItems(0);
      }
    } catch (err) {
      const message = getApiErrorMessage(
        err,
        'Failed to load collaborators'
      );
      setError(message);
      setCollaborators([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    projectId,
    currentPage,
    debouncedSearchQuery,
    statusFilters,
    sortBy,
    sortDirection,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [projectId]);

  useEffect(() => {
    fetchCollaborators();
  }, [fetchCollaborators]);

  useEffect(() => {
    if (typeof registerTabRefresh !== 'function') return undefined;
    return registerTabRefresh(fetchCollaborators);
  }, [registerTabRefresh, fetchCollaborators]);

  useEffect(() => {
    if (!collabRefreshNonce) return;
    fetchCollaborators();
  }, [collabRefreshNonce, fetchCollaborators]);

  return (
    <ProjectCollaborators
      projectId={projectId}
      collaborators={collaborators}
      isLoading={isLoading}
      error={error}
      currentPage={currentPage}
      totalItems={totalItems}
      pageSize={pageSize}
      onPageChange={setCurrentPage}
      onMemberRemoved={fetchCollaborators}
      onMemberUpdated={fetchCollaborators}
      currentLimit={collaboratorLimit}
      allocatedSlots={totalItems}
      collabModal={collabModal}
      setCollabModal={setCollabModal}
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      statusFilters={statusFilters}
      onStatusFiltersChange={handleStatusFiltersChange}
      sortBy={sortBy}
      onSortByChange={handleSortByChange}
      sortDirection={sortDirection}
      onSortDirectionChange={handleSortDirectionChange}
      canManageActions={Boolean(project?.is_current_user_active_member)}
    />
  );
};

export default ProjectCollaboratorsTab;
